import React, { useState, useEffect, useMemo } from 'react';
import { collection, getDocs, setDoc, updateDoc, deleteDoc, doc } from 'firebase/firestore';
import { getDownloadURL, ref } from 'firebase/storage';
import type { FirebaseError } from 'firebase/app';
import { PlusIcon, PencilIcon, TrashIcon } from '@heroicons/react/24/outline';
import { Link } from 'react-router-dom';
import { FileUploadInput, ImageCropModal } from '@/components/shared';
import {
    ClientLogo,
    CLIENT_LOGO_VARIANTS,
    buildClientLogoUrl,
    buildClientLogoStoragePath,
} from '@/components/ClientLogo';
import type { ClientLogoVariant } from '@/components/ClientLogo';
import type { UploadStatus } from '@/types/upload';
import { useFileUpload } from '@/hooks/form-handlers/useFileUpload';
import { useAuth, ROLE_ADMIN } from '@/contexts/AuthContext';
import { db, storage } from '@/config/firebase';

interface Client {
    id: string;
    name: string;
    logoUrl?: string | null;
    logoPath?: string | null;
    logoFileName?: string | null;
    // projectNames: string[]; // REMOVED
}


interface ClientFormData {
    name: string;
    logoUrl: string | null;
    logoPath: string | null;
    logoFileName: string | null;
    logoFile?: File | null;
}

export default function ClientsPage() {
    const { user, userHasRole } = useAuth();
    const [clients, setClients] = useState<Client[]>([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [isClientModalOpen, setIsClientModalOpen] = useState(false);
    const [editingClient, setEditingClient] = useState<Client | null>(null);

    const generateUniqueSegment = () => {
        if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
            return crypto.randomUUID();
        }
        return `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
    };

    const createInitialFormData = (): ClientFormData => ({
        name: '',
        logoUrl: null,
        logoPath: null,
        logoFileName: null,
        logoFile: null
    });

    const [formData, setFormData] = useState<ClientFormData>(() => createInitialFormData());
    const { uploadStates, resetUploadState, uploadWithFile } = useFileUpload(setFormData);
    const [logoUploadFolder, setLogoUploadFolder] = useState<string>(() => generateUniqueSegment());
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [isCropModalOpen, setIsCropModalOpen] = useState(false);
    const [pendingLogoFile, setPendingLogoFile] = useState<File | null>(null);
    const [pendingLogoOptions, setPendingLogoOptions] = useState<{ fileName: string; mimeType: string } | null>(null);
    // const [currentProjectNameInput, setCurrentProjectNameInput] = useState(''); // REMOVED

    const resetCropState = () => {
        setIsCropModalOpen(false);
        setPendingLogoFile(null);
        setPendingLogoOptions(null);
    };

    const resetFormState = () => {
        setFormData(createInitialFormData());
        resetUploadState('logoFile');
        setLogoUploadFolder(generateUniqueSegment());
        setIsSubmitting(false);
        resetCropState();
    };

    const LOGO_TARGET_SIZE = 512;

    const resolveCurrentClientId = () => editingClient?.id ?? logoUploadFolder;

    const getClientInitial = (name: string) => {
        const trimmed = name?.trim();
        if (!trimmed) return '?';
        return trimmed.charAt(0).toUpperCase();
    };

    const getFileNameFromPath = (path?: string | null) => {
        if (!path) return null;
        const segments = path.split('/');
        return segments[segments.length - 1] || null;
    };

    const getVariantPreviewSize = (variant: ClientLogoVariant) => Math.min(variant, 128);

    const sleep = (ms: number) => new Promise((resolve) => setTimeout(resolve, ms));

    const waitForProcessedLogoVariant = async (
        clientId: string,
        variant: ClientLogoVariant,
        { retries = 15, delayMs = 500 }: { retries?: number; delayMs?: number } = {}
    ): Promise<{ downloadURL: string; path: string } | null> => {
        const processedPath = buildClientLogoStoragePath(clientId, variant);
        const objectRef = ref(storage, processedPath);

        for (let attempt = 0; attempt < retries; attempt += 1) {
            try {
                const downloadURL = await getDownloadURL(objectRef);
                return { downloadURL, path: processedPath };
            } catch (rawError) {
                const error = rawError as FirebaseError;
                if (error?.code === 'storage/object-not-found' || error?.code === 'storage/unknown') {
                    await sleep(delayMs * Math.max(1, attempt + 1));
                    continue;
                }

                console.error('Erro ao obter logo redimensionada:', error);
                throw error;
            }
        }

        return null;
    };

    const canViewAdminPage = useMemo(() => {
        if (!user) return false;
        return userHasRole(ROLE_ADMIN);
    }, [user, userHasRole]);

    const loadClients = async () => {
        setLoading(true);
        try {
            const querySnapshot = await getDocs(collection(db, 'clients'));
            const clientsData = querySnapshot.docs.map(clientDoc => {
                const data = clientDoc.data() as Record<string, any>;
                return {
                    id: clientDoc.id,
                    name: data.name,
                    logoUrl: data.logoUrl ?? null,
                    logoPath: data.logoPath ?? null,
                    logoFileName: data.logoFileName ?? null
                } as Client;
            });
            setClients(clientsData);
        } catch (err) {
            console.error('Erro ao carregar clientes:', err);
            setError('Erro ao carregar clientes. Verifique o console para mais detalhes.');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        if (canViewAdminPage) {
            loadClients();
        }
    }, [canViewAdminPage]);

    const handleClientNameChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        setFormData(prev => ({ ...prev, name: e.target.value }));
    };

    const handleLogoChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0];
        // Permite selecionar o mesmo arquivo novamente
        e.target.value = '';

        if (!file) {
            return;
        }

        setError(null);
        const targetClientId = resolveCurrentClientId();

        setPendingLogoFile(file);
        setPendingLogoOptions({ fileName: targetClientId, mimeType: 'image/png' });
        setIsCropModalOpen(true);
    };

    const handleCropConfirm = async (croppedFile: File) => {
        const previousLogoState = {
            logoUrl: formData.logoUrl,
            logoPath: formData.logoPath,
            logoFileName: formData.logoFileName
        };

        resetUploadState('logoFile');

        try {
            const targetClientId = resolveCurrentClientId();
            const targetFileName = `${targetClientId}.png`;
            const storagePath = `clients/${targetClientId}/logo/${targetFileName}`;

            const fileForUpload =
                croppedFile.name === targetFileName
                    ? croppedFile
                    : new File([croppedFile], targetFileName, { type: croppedFile.type || 'image/png' });

            const downloadURL = await uploadWithFile(fileForUpload, {
                fieldName: 'logoFile',
                urlFieldName: 'logoUrl',
                pathPrefix: 'clients',
                buildPath: () => storagePath,
                onUploadComplete: ({ path }) => {
                    setFormData(prev => ({
                        ...prev,
                        logoPath: path,
                        logoFileName: targetFileName
                    }));
                }
            });

            if (!downloadURL) {
                throw new Error('Falha ao enviar logo do cliente. Tente novamente.');
            }

            const processedLogo = await waitForProcessedLogoVariant(targetClientId, 512);

            if (!processedLogo) {
                console.warn('Logo processada não encontrada após o upload:', storagePath);
                throw new Error('A logo está sendo processada. Ela ficará disponível em instantes, tente novamente em alguns segundos.');
            }

            setFormData(prev => ({
                ...prev,
                logoUrl: processedLogo.downloadURL,
                logoPath: processedLogo.path,
                logoFileName: targetFileName
            }));

            setError(null);
            resetCropState();
        } catch (uploadError: any) {
            console.error('Erro ao enviar logo recortado:', uploadError);
            const message = uploadError?.message || 'Não foi possível enviar a logo recortada. Tente novamente.';
            setFormData(prev => ({
                ...prev,
                ...previousLogoState
            }));
            setError(message);
            throw new Error(message);
        } finally {
            resetUploadState('logoFile');
        }
    };

    const logoUploadState = uploadStates['logoFile'];
    const isLogoUploading = logoUploadState?.status === 'uploading';
    const hasPersistedLogo = Boolean(formData.logoUrl);
    const derivedLogoStatus: UploadStatus =
        logoUploadState?.status === 'error'
            ? 'error'
            : isLogoUploading
                ? 'uploading'
                : hasPersistedLogo
                    ? 'success'
                    : (logoUploadState?.status ?? 'idle');
    const derivedLogoProgress = isLogoUploading
        ? Math.round(logoUploadState?.progress ?? 0)
        : hasPersistedLogo
            ? 100
            : undefined;
    const derivedLogoError = logoUploadState?.status === 'error' ? logoUploadState.error : null;
    const baseLogoUrl = isLogoUploading ? undefined : (logoUploadState?.downloadURL ?? formData.logoUrl ?? undefined);
    const derivedLogoUrl = baseLogoUrl ? buildClientLogoUrl(baseLogoUrl, 512) ?? undefined : undefined;
    const derivedLogoFileName = formData.logoFileName || getFileNameFromPath(formData.logoPath) || undefined;

    // REMOVED handleCurrentProjectNameInputChange
    // REMOVED handleAddProjectName
    // REMOVED handleRemoveProjectName

    const openClientModal = (client: Client | null = null) => {
        setIsSubmitting(false);
        resetUploadState('logoFile');
        resetCropState();
        if (client) {
            setEditingClient(client);
            setLogoUploadFolder(client.id);
            setFormData({
                name: client.name,
                logoUrl: client.logoUrl ?? null,
                logoPath: client.logoPath ?? null,
                logoFileName: client.logoFileName ?? null,
                logoFile: null
            });
        } else {
            setEditingClient(null);
            setLogoUploadFolder(generateUniqueSegment());
            setFormData(createInitialFormData());
        }
        setIsClientModalOpen(true);
        setError(null);
    };

    const closeClientModal = () => {
        setIsClientModalOpen(false);
        setEditingClient(null);
        resetFormState();
        setError(null);
    };

    const handleClientSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setError(null);
        if (!formData.name.trim()) {
            setError("O nome do cliente é obrigatório.");
            return;
        }

        if (isLogoUploading) {
            setError('Aguarde o envio da logo ser concluído antes de salvar.');
            return;
        }

        setIsSubmitting(true);

        const clientData = {
            name: formData.name.trim(),
            logoUrl: formData.logoUrl ?? null,
            logoPath: formData.logoPath ?? null,
            logoFileName: formData.logoFileName ?? null
        };

        try {
            if (editingClient) {
                await updateDoc(doc(db, 'clients', editingClient.id), clientData);
            } else {
                const newClientId = logoUploadFolder;
                await setDoc(doc(db, 'clients', newClientId), clientData);
            }
            await loadClients();
            closeClientModal();
        } catch (err) {
            console.error('Erro ao salvar cliente:', err);
            setError('Erro ao salvar cliente. Verifique o console para mais detalhes.');
        } finally {
            setIsSubmitting(false);
        }
    };

    const handleDeleteClient = async (clientId: string) => {
        if (window.confirm('Tem certeza que deseja excluir este cliente? Esta ação não pode ser desfeita.')) {
            try {
                await deleteDoc(doc(db, 'clients', clientId));
                await loadClients();
            } catch (err) {
                console.error('Erro ao excluir cliente:', err);
                setError('Erro ao excluir cliente. Verifique o console para mais detalhes.');
            }
        }
    };

    if (!user && loading) {
        return <div className="text-gray-500 p-4">Carregando informações do usuário...</div>;
    }

    if (!canViewAdminPage && !loading) {
        return (
            <div className="flex justify-center items-center h-screen">
                <div className="text-center p-4">
                    <h1 className="text-2xl font-semibold text-gray-700 mb-4">Acesso Negado</h1>
                    <p className="text-gray-500">Você não tem permissão para visualizar esta página.</p>
                    <Link to="/" className="mt-6 inline-block bg-indigo-600 text-white px-6 py-2 rounded hover:bg-indigo-700">
                        Voltar para o Dashboard
                    </Link>
                </div>
            </div>
        );
    }

    if (loading) {
        return <div className="text-gray-500 p-4">Carregando clientes...</div>;
    }

    return (
        <>
            <div className="space-y-6">
                <div className="sm:flex sm:items-center sm:justify-between">
                    <div>
                        <h2 className="text-2xl font-bold leading-7 text-gray-900 sm:truncate sm:text-3xl sm:tracking-tight">
                            Gerenciamento de Clientes
                        </h2>
                        <p className="mt-1 text-sm text-gray-500">
                            Adicione, edite ou remova clientes.
                        </p>
                    </div>
                    <div className="mt-4 sm:mt-0">
                        <button
                            type="button"
                            onClick={() => openClientModal()}
                            className="btn-primary"
                        >
                            <PlusIcon className="h-5 w-5 mr-2" />
                            Novo Cliente
                        </button>
                    </div>
                </div>

                {error && (
                    <div className="rounded-md bg-red-50 p-4 my-4">
                        <div className="flex">
                            <div className="ml-3">
                                <h3 className="text-sm font-medium text-red-800">Erro</h3>
                                <div className="mt-2 text-sm text-red-700">
                                    <p>{error}</p>
                                </div>
                            </div>
                        </div>
                    </div>
                )}

                {/* Client List Table */}
                <div className="overflow-x-auto bg-white shadow sm:rounded-lg">
                    <table className="min-w-full divide-y divide-gray-200">
                        <thead className="bg-gray-50">
                            <tr>
                                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Logo</th>
                                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Nome do Cliente</th>
                                {/* <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Projetos Associados</th> // REMOVED */}
                                <th scope="col" className="relative px-6 py-3">
                                    <span className="sr-only">Ações</span>
                                </th>
                            </tr>
                        </thead>
                        <tbody className="bg-white divide-y divide-gray-200">
                            {clients.length === 0 && !loading ? (
                                <tr>
                                    <td colSpan={3} className="px-6 py-12 text-center text-sm text-gray-500"> {/* Adjusted colSpan */}
                                        Nenhum cliente cadastrado.
                                    </td>
                                </tr>
                            ) : (
                                clients.map((client) => (
                                    <tr key={client.id}>
                                        <td className="px-6 py-4">
                                            <div className="flex flex-wrap items-start gap-4">
                                                {CLIENT_LOGO_VARIANTS.map((variant) => {
                                                    const previewSize = getVariantPreviewSize(variant);
                                                    return (
                                                        <div key={`${client.id}-${variant}`} className="flex flex-col items-center gap-1">
                                                            <div
                                                                className="flex items-center justify-center overflow-hidden rounded border border-gray-200 bg-white p-1"
                                                                style={{ width: `${previewSize}px`, height: `${previewSize}px` }}
                                                            >
                                                                <ClientLogo
                                                                    client={{ id: client.id, name: client.name, logoUrl: client.logoUrl ?? null }}
                                                                    clientId={client.id}
                                                                    variant={variant}
                                                                    className="h-full w-full"
                                                                />
                                                            </div>
                                                            <span className="text-[10px] font-medium text-gray-400">{variant}px</span>
                                                        </div>
                                                    );
                                                })}
                                            </div>
                                        </td>
                                            <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">{client.name}</td>
                                            <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium space-x-2">
                                                <button onClick={() => openClientModal(client)} className="text-indigo-600 hover:text-indigo-900" title="Editar Cliente">
                                                    <PencilIcon className="h-5 w-5" />
                                                </button>
                                                <button onClick={() => handleDeleteClient(client.id)} className="text-red-600 hover:text-red-900" title="Excluir Cliente">
                                                    <TrashIcon className="h-5 w-5" />
                                                </button>
                                            </td>
                                        </tr>
                                ))
                            )}
                        </tbody>
                    </table>
                </div>

                {/* Client Modal Form */}
                {isClientModalOpen && (
                    <div className="fixed inset-0 z-50 overflow-y-auto bg-gray-600 bg-opacity-75 transition-opacity">
                        <div className="flex min-h-full items-center justify-center p-4 text-center">
                            <div className="relative bg-white rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:max-w-lg sm:w-full">
                                <form onSubmit={handleClientSubmit}>
                                    <div className="bg-white px-4 pt-5 pb-4 sm:p-6 sm:pb-4">
                                        <div className="w-full"> {/* Ensure full width for inner content */}
                                            <h3 className="text-lg leading-6 font-medium text-gray-900 mb-4" id="modal-title">
                                                {editingClient ? 'Editar Cliente' : 'Adicionar Novo Cliente'}
                                            </h3>
                                            {error && (
                                                <div className="rounded-md bg-red-50 p-3 my-3">
                                                    <p className="text-sm text-red-700">{error}</p>
                                                </div>
                                            )}
                                            <div className="space-y-4">
                                                <div>
                                                    <label htmlFor="clientName" className="block text-sm font-medium text-gray-700">
                                                        Nome do Cliente <span className="text-red-500">*</span>
                                                    </label>
                                                    <input
                                                        type="text"
                                                        name="clientName"
                                                        id="clientName"
                                                        value={formData.name}
                                                        onChange={handleClientNameChange}
                                                        className="input-primary mt-1 w-full"
                                                        required
                                                    />
                                                </div>
                                                <div>
                                                    <FileUploadInput
                                                        name="clientLogo"
                                                        label="Logo do Cliente"
                                                        fileUrl={derivedLogoUrl}
                                                        fileName={derivedLogoFileName}
                                                        isSubmitting={isSubmitting || isLogoUploading}
                                                        onChange={handleLogoChange}
                                                        accept="image/*"
                                                        progress={derivedLogoProgress}
                                                        status={derivedLogoStatus}
                                                        error={derivedLogoError || undefined}
                                                    />
                                                    <p className="mt-1 text-xs text-gray-500">
                                                        Recomendamos imagens quadradas em PNG, JPG ou SVG.
                                                    </p>
                                                </div>
                                                <div>
                                                    <p className="text-xs font-semibold uppercase text-gray-500">Pré-visualização das variantes</p>
                                                    <div className="mt-2 flex flex-wrap items-start gap-4">
                                                        {CLIENT_LOGO_VARIANTS.map((variant) => {
                                                            const previewSize = getVariantPreviewSize(variant);
                                                            const clientId = resolveCurrentClientId();
                                                            return (
                                                                <div key={`preview-${variant}`} className="flex flex-col items-center gap-1">
                                                                    <div
                                                                        className="flex items-center justify-center overflow-hidden rounded border border-gray-200 bg-white p-1"
                                                                        style={{ width: `${previewSize}px`, height: `${previewSize}px` }}
                                                                    >
                                                                        <ClientLogo
                                                                            client={{ id: clientId, name: formData.name || null, logoUrl: formData.logoUrl ?? derivedLogoUrl ?? null }}
                                                                            clientId={clientId}
                                                                            variant={variant}
                                                                            className="h-full w-full"
                                                                        />
                                                                    </div>
                                                                    <span className="text-[10px] font-medium text-gray-400">{variant}px</span>
                                                                </div>
                                                            );
                                                        })}
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                    <div className="bg-gray-50 px-4 py-3 sm:px-6 sm:flex sm:flex-row-reverse">
                                        <button
                                            type="submit"
                                            className="btn-primary w-full sm:ml-3 sm:w-auto disabled:opacity-60 disabled:cursor-not-allowed"
                                            disabled={isSubmitting || isLogoUploading}
                                        >
                                            {editingClient ? 'Salvar Alterações' : 'Adicionar Cliente'}
                                        </button>
                                        <button
                                            type="button"
                                            onClick={closeClientModal}
                                            className="btn-secondary-outline mt-3 w-full sm:mt-0 sm:w-auto"
                                        >
                                            Cancelar
                                        </button>
                                    </div>
                                </form>
                            </div>
                        </div>
                    </div>
                )}
            </div>

            <ImageCropModal
                isOpen={isCropModalOpen}
                file={pendingLogoFile}
                onCancel={resetCropState}
                onConfirm={handleCropConfirm}
                outputFileName={pendingLogoOptions?.fileName}
                outputMimeType={pendingLogoOptions?.mimeType}
                title="Ajustar logo do cliente"
                description={`A imagem final será salva em ${LOGO_TARGET_SIZE}x${LOGO_TARGET_SIZE}px.`}
                aspect={1}
                targetSize={LOGO_TARGET_SIZE}
                zoomRange={{ min: 0.1, max: 4, step: 0.01 }}
                confirmLabel="Aplicar recorte"
                cancelLabel="Cancelar"
            />
        </>
    );
}
